import { Application, Container, Graphics } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import { SessionRecorder, TouchTracker, detectMultiTouch } from "@talent/client-telemetry";
import { orderingPrefixValid, scoreOrderingTrial, type OrderingRound, DEMO_FORM } from "@talent/tasks";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  easeOutCubic,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, lighten, mix, touchMin } from "./paint.js";

/**
 * Smallest to biggest — tap the items in order of size.
 *
 * **The tapped items build a staircase.** Each one taken lands in a row along the bottom, in the
 * order it was chosen, so the answer is a thing being assembled rather than a sequence of taps a
 * child has to hold in their head. It also makes a mistake legible: the staircase visibly stops
 * climbing.
 *
 * **A wrong tap does not end the round.** The item wobbles, the smallest remaining one glows, and
 * the child carries on. Ending it would score a child on their first slip in a task whose whole
 * content is careful comparison, and would teach them that looking again is not allowed.
 */

export interface OrderingTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateRound: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => OrderingRound;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

interface Attempt {
  tapped: number[];
  mistakes: number;
}

export async function runOrderingTask(opts: RunOptions): Promise<OrderingTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    backdrop.circle(w / 2, h * 0.38, Math.min(w, h) * 0.5).fill({ color: accent, alpha: 0.07 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  function present(round: OrderingRound): {
    attempt: Promise<Attempt>;
    simulateTap: (position: number) => void;
    relayout: () => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);
    const shelf = new Container();
    const staircase = new Graphics();
    layer.addChild(shelf, staircase);

    const tapped: number[] = [];
    let mistakes = 0;
    let accepting = true;
    let busy = false;
    let resolveAttempt: ((a: Attempt) => void) | null = null;

    const items: Graphics[] = [];
    /** Biggest an item can be drawn, and the floor a small one never goes under. */
    let maxRadius = 0;
    let minRadius = 0;
    let stairY = 0;

    /** A blob, sized by its value. The same creature-ish highlight every other surface gets. */
    function drawItem(g: Graphics, size: number, state: "idle" | "taken" | "wrong"): void {
      const r = minRadius + (maxRadius - minRadius) * size;
      const color =
        state === "taken" ? COLOR.correct : state === "wrong" ? COLOR.wrong : accent;
      g.clear();
      g.circle(0, 0, r).fill(color);
      g.circle(-r * 0.28, -r * 0.32, r * 0.22).fill({ color: 0xffffff, alpha: 0.42 });
      if (state === "taken") g.circle(0, 0, r).stroke({ width: 3, color: COLOR.correct });
    }

    /** The answer so far, laid out along the bottom in the order it was chosen. */
    function drawStaircase(): void {
      const w = app.renderer.width;
      const total = round.sizes.length;
      const slot = Math.min(46, (w * 0.86) / total);
      let x = (w - slot * (total - 1)) / 2;
      staircase.clear();
      for (let i = 0; i < total; i++) {
        const position = tapped[i];
        if (position === undefined) {
          // An empty socket, so the row reads as somewhere things go.
          staircase.circle(x, stairY, 6).fill({ color: COLOR.card, alpha: 0.18 });
        } else {
          const size = round.sizes[position] ?? 0;
          const r = 7 + 11 * size;
          staircase.circle(x, stairY, r).fill(COLOR.correct);
          staircase.circle(x - r * 0.3, stairY - r * 0.3, r * 0.24).fill({
            color: 0xffffff,
            alpha: 0.45,
          });
        }
        x += slot;
      }
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const count = round.sizes.length;

      stairY = h - 46;
      // Columns bounded by the touch floor, so a small item is never smaller than a finger.
      const gap = Math.max(10, w * 0.03);
      const columns = Math.max(2, Math.min(count, Math.floor((w * 0.9 + gap) / (touchMin() + gap))));
      const rows = Math.ceil(count / columns);
      const cell = Math.min((w * 0.9 - gap * (columns - 1)) / columns, (h * 0.52) / rows);
      maxRadius = cell * 0.46;
      // A floor, not a fraction of the biggest: at the hardest level the smallest would otherwise
      // fall under the touch minimum, and a child would be missing it for reasons of aim.
      minRadius = Math.max(touchMin() * 0.42, maxRadius * 0.4);

      const gridW = cell * columns + gap * (columns - 1);
      const startX = (w - gridW) / 2 + cell / 2;
      const startY = h * 0.3 - ((rows - 1) * (cell + gap)) / 2;

      shelf.removeChildren().forEach((c) => c.destroy());
      items.length = 0;

      round.sizes.forEach((size, position) => {
        const g = new Graphics();
        g.position.set(
          startX + (position % columns) * (cell + gap),
          startY + Math.floor(position / columns) * (cell + gap),
        );
        const taken = tapped.includes(position);
        drawItem(g, size, taken ? "taken" : "idle");
        g.alpha = taken ? 0.35 : 1;
        g.eventMode = "static";
        g.cursor = "pointer";
        const half = Math.max(touchMin(), cell) / 2;
        g.hitArea = {
          contains: (x: number, y: number) => x >= -half && y >= -half && x <= half && y <= half,
        };
        g.on("pointertap", () => choose(position));
        items.push(g);
        shelf.addChild(g);
      });

      drawStaircase();
    }

    function choose(position: number): void {
      if (!accepting || busy) return;
      if (tapped.includes(position)) {
        tick();
        return;
      }
      const g = items[position];
      if (!g) return;

      const wouldBe = [...tapped, position];
      const right = orderingPrefixValid(round, wouldBe);
      busy = true;

      void (async () => {
        ripple(fx, g.position.x, g.position.y, accent, maxRadius * 2);

        if (!right) {
          mistakes++;
          missTone();
          drawItem(g, round.sizes[position] ?? 0, "wrong");
          await nudge(g, 6);
          drawItem(g, round.sizes[position] ?? 0, "idle");

          // Show which one WAS smallest. The round continues: ending it would score a child on
          // their first slip in a task whose whole content is careful comparison.
          const answer = round.answer[tapped.length];
          const target = answer === undefined ? undefined : items[answer];
          if (target && answer !== undefined) {
            glow(glowLayer, target.position.x, target.position.y, maxRadius * 1.3, COLOR.correct, 700);
            cellTone(3, { volume: 0.09 });
            await pop(target, { scale: 1.16 });
          }
          await idle(300);
          busy = false;
          return;
        }

        tapped.push(position);
        cellTone(tapped.length + 2);
        drawItem(g, round.sizes[position] ?? 0, "taken");
        void pop(g, { scale: 1.12 });
        if (!prefersReducedMotion()) {
          await animate(200, (t) => {
            if (!g.destroyed) g.alpha = 1 - 0.65 * easeOutCubic(t);
          });
        }
        if (!g.destroyed) g.alpha = 0.35;
        drawStaircase();

        if (tapped.length === round.sizes.length) {
          accepting = false;
          successChime();
          burst(fx, app.renderer.width / 2, stairY, [accent, lighten(accent, 0.4), COLOR.correct], 20);
          await idle(620);
          resolveAttempt?.({ tapped: [...tapped], mistakes });
          return;
        }
        busy = false;
      })();
    }

    layout();
    if (!prefersReducedMotion()) {
      items.forEach((g, i) => {
        g.scale.set(0);
        void idle(60 + i * 55).then(() => animate(280, (t) => {
          if (!g.destroyed) g.scale.set(easeOutBack(t));
        }));
      });
    }

    return {
      attempt: new Promise<Attempt>((resolve) => {
        resolveAttempt = resolve;
      }),
      simulateTap: choose,
      relayout: layout,
      cleanup: () => {
        accepting = false;
        layer.destroy({ children: true });
      },
    };
  }

  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctRounds = 0;
  let reason: OrderingTaskResult["reason"] = "completed";
  let onResize: (() => void) | null = null;

  /**
   * A wordless demonstration: the game taps a whole easy round, smallest first.
   *
   * "Smallest first" is a rule, not a picture, and nothing on the board states it — three blobs are
   * just three blobs. Watching the staircase build from the small end says it in one pass.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generateRound(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(demo);
      await idle(900);
      for (const position of demo.answer) {
        if (opts.signal?.aborted) break;
        shown.simulateTap(position);
        await idle(750);
      }
      if (!opts.signal?.aborted) {
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const round = opts.generateRound(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // Sizes are fractions; scaled to integers so the record stays re-scorable without
        // depending on how a client happened to round them.
        stimulus: round.sizes.map((s) => Math.round(s * 1000)),
        options: [round.answer],
        correctIndex: 0,
      });

      const start = performance.now();
      const trial = present(round);
      onResize = trial.relayout;
      app.renderer.on("resize", trial.relayout);

      const outcome = await untilSettled(trial.attempt, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      app.renderer.off("resize", trial.relayout);
      onResize = null;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      // The round only ends when the order is complete, so the finished sequence is always right;
      // what separates children is how many wrong taps it took to get there.
      const clean = outcome.mistakes === 0 && scoreOrderingTrial(round, outcome.tapped);
      if (clean) correctRounds++;

      await recorder.record("trial_response", {
        trialIndex,
        response: outcome.tapped,
        correct: clean,
        responseMs: elapsed,
        // Wrong taps on the way. The round always completes, so this is the signal.
        corrections: outcome.mistakes,
      });

      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, clean);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(320);
    }
  } catch {
    reason = "error";
  } finally {
    if (onResize) app.renderer.off("resize", onResize);
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctRounds / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctRounds,
    rawScore: trialIndex > 0 ? correctRounds / trialIndex : 0,
  };
}
